import CartDetails from "../../../components/cart/CartDetails";
import {useStoreon} from "@storeon/solidjs";
import {createEffect, createMemo, createSignal, For, Suspense} from "solid-js";
import {Accordion, Alert, Button, Form} from "solid-bootstrap";
import FontAwesomeIcon from "../../../components/fontawesome/FontAwesomeIcon";
import {faLock, faWarning} from "@fortawesome/pro-light-svg-icons";
import createPrice from "../../../helpers/createPrice";
import {useNavigate} from "@solidjs/router";
import DuplicateBookingModal from "../../../components/DuplicateBookingModal";
import duplicateKey from "../../../helpers/duplicateKey";

export default props => {
    const [state, dispatch] = useStoreon();
    const navigate = useNavigate();
    const [paymentError, setPaymentError] = createSignal();
    const [pendingDuplicate, setPendingDuplicate] = createSignal(null);
    const [pendingPaymentCode, setPendingPaymentCode] = createSignal(null);

    createEffect(() => dispatch("loadPaymentMethods"));
    const paymentMethods = createMemo(() => state.paymentMethods[state.token] || []);

    const onSelect = (eventKey) => eventKey !== null && (document.querySelector(`#payment-method-${eventKey}`).checked = true);

    async function doPayment(code) {

        // Contrôle des doublons avant tout paiement (tous utilisateurs).
        // Les utilisateurs connectés sont identifiés par leur session ; les
        // anonymes par l'email du client de la commande.
        const lines = (state.cart && state.cart.lines) || [];
        const email = state.currentUser ? null : (state.cart && state.cart.customer && state.cart.customer.emailAddress);
        for (const line of lines) {
            if (!line.customFields || !line.customFields.schedule) continue;
            const scheduleId = line.customFields.schedule.id;
            const date = line.customFields.date;
            const key = duplicateKey(scheduleId, date);
            if (state.confirmedDuplicates[key]) {
                continue;
            }
            const result = await state.agents.cart.checkDuplicate({ scheduleId, date, email });
            // Fail-open : si la vérification échoue (erreur réseau, etc.),
            // on laisse passer — la détection est indicative, jamais bloquante.
            if (result && !result.graphQLErrors && result.isDuplicate) {
                setPendingPaymentCode(code);
                setPendingDuplicate({
                    key,
                    slotInfo: {
                        activityName: line.productVariant.name,
                        date: line.customFields.date,
                        schedule: {
                            from: line.customFields.schedule.from,
                            to: line.customFields.schedule.to,
                            utcOffset: line.customFields.schedule.utcOffset
                        }
                    }
                });
                return;
            }
        }

        await processPayment(code);
    }

    async function processPayment(code) {

        // PayFiP
        if (code.indexOf("payfip") !== -1) {
            const result = await state.agents.cart.createPayFipPaymentIntent(code);
            if (result.errorCode) {
                setPaymentError(result.message);
                return;
            } else {
                setPaymentError(null);
                if (result.url) {

                    window.location.href = result.url;
                    return;
                }
            }
        }

        // Test
        else if (code.indexOf("test") !== -1) {
            const result = await state.agents.cart.createTestPaymentIntent(code);
            if (result.errorCode) {
                setPaymentError(result.message);
                return;
            } else {
                setPaymentError(null);
            }
        }

        // Sur place
        else if (code.indexOf("sur-place") !== -1) {
            const result = await state.agents.cart.addPaymentToOrder(code);
            if (result.errorCode) {
                setPaymentError(result.message);
                return;
            } else {
                setPaymentError(null);
            }
        }

        const currentCode = state.cart.code;

        const cart = await state.agents.cart.getCurrent();
        dispatch("setCart", cart);

        navigate("/order/confirm/" + currentCode);

    }

    const confirmDuplicate = async () => {
        const pending = pendingDuplicate();
        if (pending) {
            dispatch("confirmDuplicate", pending.key);
        }
        const code = pendingPaymentCode();
        setPendingDuplicate(null);
        setPendingPaymentCode(null);
        if (code) {
            await processPayment(code);
        }
    };

    const cancelDuplicate = () => {
        setPendingDuplicate(null);
        setPendingPaymentCode(null);
    };

    return (
        <div class="customer">
            <h2 class="mb-4 text-center">Choix du paiement</h2>
            <p class="mb-4 text-center">En validant ma commande, je déclare avoir pris connaissance et accepter sans réserve : <a href="">Les CGV</a></p>
            <div class="row justify-content-center">
                <div class="col-12 col-md-8">
                    <Show when={paymentError()}>
                        <Alert variant="danger"><FontAwesomeIcon icon={faWarning} /> {paymentError()}</Alert>
                    </Show>
                    <Suspense fallback="Chargement des méthodes de paiement en cours...">
                        <Accordion defaultActiveKey={0} onSelect={onSelect}>
                            <For each={paymentMethods()} fallback="Aucune méthode de paiement n'a été trouvée">{(method, index) => (
                                <Accordion.Item eventKey={index()}>
                                    <Accordion.Header>
                                        <Form.Check type="radio" label="" name="payment_method" id={`payment-method-${index()}`} checked={index() === 0} />
                                        <span class="ml-4">{method.name}</span>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <Show when={method.description.length > 0}>
                                            <div class="mb-4" innerHTML={method.description} />
                                            <Show when={method.code.indexOf("payfip") !== -1}>
                                                <hr class="mb-4" />
                                            </Show>
                                        </Show>
                                        <Show when={method.code.indexOf("payfip") !== -1}>
                                            <div class="mb-4">En cliquant sur "Payer" vous serez redirigé vers le site de PayFiP.</div>
                                        </Show>
                                        <Button variant="primary" size="lg" onClick={() => doPayment(method.code)}>
                                            <FontAwesomeIcon icon={faLock}></FontAwesomeIcon>&nbsp;
                                            <Show when={state.cart && state.cart.totalWithTax > 0 && method.code.indexOf("payfip") !== -1} fallback="Valider ma commande">Payer {createPrice(state.cart.totalWithTax)} €</Show>
                                        </Button>
                                    </Accordion.Body>
                                </Accordion.Item>
                            )}</For>
                        </Accordion>
                    </Suspense>
                </div>
                <div class="col-12 col-md-4 mt-4 mt-md-0">
                    <CartDetails order={true} secured={true} validate={false} />
                </div>
            </div>
            <DuplicateBookingModal
                show={!!pendingDuplicate()}
                slotInfo={pendingDuplicate() && pendingDuplicate().slotInfo}
                isAnonymous={!state.currentUser}
                onConfirm={confirmDuplicate}
                onCancel={cancelDuplicate}
            />
        </div>
    );
}